import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ROUTE_META, inputClass, labelClass, buttonPrimary, buttonGhost, errorMessage } from "./aiRoutes";

const BLANK_EDITOR = {
  id: null,
  name: "",
  route: "safe",
  description: "",
  system_prompt: "",
  user_prompt: "",
};

/**
 * Built-in and custom prompt templates. Each template belongs to one route,
 * and running it always uses that route (no cross-mode runs).
 */
export default function PromptTemplates() {
  const [filter, setFilter] = useState("all");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [runVars, setRunVars] = useState({});
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { route: filter };
      const { data } = await api.get("/api/ai/templates", { params });
      setTemplates(data.templates || []);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load templates"));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(() => templates.find((t) => t.id === selectedId) || null, [templates, selectedId]);

  const selectTemplate = (t) => {
    setSelectedId(t.id);
    setEditor(null);
    setRunResult(null);
    setRunVars({});
  };

  const startNew = () => {
    setEditor({ ...BLANK_EDITOR, route: filter === "explicit" ? "explicit" : "safe" });
  };

  const startDuplicate = (t) => {
    setEditor({
      id: null,
      name: `Copy of ${t.name}`.slice(0, 120),
      route: t.route,
      description: t.description || "",
      system_prompt: t.system_prompt || "",
      user_prompt: t.user_prompt || "",
    });
  };

  const startEdit = (t) => {
    setEditor({
      id: t.id,
      name: t.name,
      route: t.route,
      description: t.description || "",
      system_prompt: t.system_prompt || "",
      user_prompt: t.user_prompt || "",
    });
  };

  const saveEditor = async () => {
    if (!editor.name.trim() || !editor.user_prompt.trim()) {
      toast.error("Name and user prompt are required");
      return;
    }
    const body = {
      name: editor.name.trim(),
      route: editor.route,
      description: editor.description.trim(),
      system_prompt: editor.system_prompt,
      user_prompt: editor.user_prompt,
    };
    setSaving(true);
    try {
      if (editor.id) {
        await api.put(`/api/ai/templates/${editor.id}`, body);
        toast.success("Template saved");
      } else {
        const { data } = await api.post("/api/ai/templates", body);
        setSelectedId(data.template.id);
        toast.success("Template created");
      }
      setEditor(null);
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/api/ai/templates/${t.id}`);
      toast.success("Template deleted");
      setSelectedId(null);
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Delete failed"));
    }
  };

  const runTemplate = async () => {
    if (!selected) return;
    setRunning(true);
    setRunResult(null);
    try {
      const { data } = await api.post(`/api/ai/templates/${selected.id}/run`, {
        mode: selected.route,
        variables: runVars,
      });
      setRunResult(data);
    } catch (e) {
      toast.error(errorMessage(e, "Template run failed"));
    } finally {
      setRunning(false);
    }
  };

  const isBuiltin = (t) => t.builtin || String(t.id).startsWith("builtin-");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      {/* List */}
      <div className="lg:col-span-2 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="inline-flex rounded-lg border border-zinc-700 bg-zinc-950 p-0.5">
            {["all", "safe", "explicit"].map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
                  filter === f ? "bg-zinc-700 text-white" : "text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {f === "all" ? "All" : ROUTE_META[f].label}
              </button>
            ))}
          </div>
          <button type="button" className={buttonPrimary} onClick={startNew}>New template</button>
        </div>

        {loading && <div className="text-xs text-zinc-500">Loading…</div>}
        <ul className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {templates.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() => selectTemplate(t)}
                className={`w-full text-left border rounded-lg px-3 py-2 transition ${
                  selectedId === t.id ? "border-purple-500 bg-purple-900/20" : "border-zinc-800 hover:border-zinc-600"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-zinc-100 truncate">{t.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${t.route === "explicit" ? "bg-rose-900/40 text-rose-300" : "bg-emerald-900/40 text-emerald-300"}`}>
                    {ROUTE_META[t.route]?.label}
                  </span>
                  {isBuiltin(t) && <span className="text-[10px] text-zinc-500">built-in</span>}
                </div>
                {t.description && <div className="text-xs text-zinc-500 mt-0.5 truncate">{t.description}</div>}
              </button>
            </li>
          ))}
          {!loading && templates.length === 0 && <li className="text-xs text-zinc-500">No templates.</li>}
        </ul>
      </div>

      {/* Detail / editor */}
      <div className="lg:col-span-3 border border-zinc-800 rounded-lg p-4 bg-zinc-950/40 min-h-[260px]">
        {editor && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className={labelClass}>Name</label>
                <input className={inputClass} value={editor.name} maxLength={120}
                  onChange={(e) => setEditor({ ...editor, name: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Route</label>
                <select className={inputClass} value={editor.route}
                  onChange={(e) => setEditor({ ...editor, route: e.target.value })}>
                  <option value="safe">Safe</option>
                  <option value="explicit">Explicit</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <input className={inputClass} value={editor.description} maxLength={500}
                onChange={(e) => setEditor({ ...editor, description: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>System prompt</label>
              <textarea rows={3} className={inputClass} value={editor.system_prompt}
                onChange={(e) => setEditor({ ...editor, system_prompt: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>User prompt</label>
              <textarea rows={4} className={inputClass} value={editor.user_prompt}
                onChange={(e) => setEditor({ ...editor, user_prompt: e.target.value })} />
              <p className="text-[11px] text-zinc-500 mt-1">Use {"{{variable}}"} placeholders. Each one becomes an input when you run the template.</p>
            </div>
            <div className="flex gap-2">
              <button type="button" className={buttonPrimary} onClick={saveEditor} disabled={saving}>
                {saving ? "Saving…" : editor.id ? "Save changes" : "Create template"}
              </button>
              <button type="button" className={buttonGhost} onClick={() => setEditor(null)}>Cancel</button>
            </div>
          </div>
        )}

        {!editor && selected && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-zinc-100">{selected.name}</div>
                <div className="text-xs text-zinc-500">
                  {ROUTE_META[selected.route]?.title} · {selected.route === "explicit" ? "runs only in Explicit mode" : "runs only in Safe mode"}
                </div>
              </div>
              <div className="flex gap-2">
                <button type="button" className={buttonGhost} onClick={() => startDuplicate(selected)}>Duplicate</button>
                {!isBuiltin(selected) && (
                  <>
                    <button type="button" className={buttonGhost} onClick={() => startEdit(selected)}>Edit</button>
                    <button type="button" className={buttonGhost} onClick={() => deleteTemplate(selected)}>Delete</button>
                  </>
                )}
              </div>
            </div>

            {selected.system_prompt && (
              <div>
                <div className={labelClass}>System prompt</div>
                <pre className="text-xs whitespace-pre-wrap text-zinc-300 bg-zinc-950 border border-zinc-800 rounded p-3">{selected.system_prompt}</pre>
              </div>
            )}
            <div>
              <div className={labelClass}>User prompt</div>
              <pre className="text-xs whitespace-pre-wrap text-zinc-300 bg-zinc-950 border border-zinc-800 rounded p-3">{selected.user_prompt}</pre>
            </div>

            <div className="border-t border-zinc-800 pt-4 space-y-3">
              <div className="text-sm font-semibold text-zinc-200">Run template</div>
              {(selected.variables || []).length === 0 && (
                <p className="text-xs text-zinc-500">This template has no variables.</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(selected.variables || []).map((v) => (
                  <div key={v}>
                    <label className={labelClass} htmlFor={`tpl-var-${v}`}>{v}</label>
                    <input id={`tpl-var-${v}`} className={inputClass} value={runVars[v] || ""}
                      onChange={(e) => setRunVars({ ...runVars, [v]: e.target.value })} />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <button type="button" className={buttonPrimary} onClick={runTemplate} disabled={running}>
                  {running ? "Running…" : "Run"}
                </button>
                <span className="text-xs text-zinc-500">Mode: {ROUTE_META[selected.route]?.label} (locked to this template)</span>
              </div>
              {runResult && (
                <div className="space-y-2">
                  <div className="text-[11px] text-zinc-500">
                    {runResult.provider} · {runResult.model}
                    {runResult.fallback_used ? " (fallback used)" : ""} · {runResult.latency_ms} ms
                  </div>
                  <pre className="text-sm whitespace-pre-wrap text-zinc-100 bg-zinc-950 border border-zinc-800 rounded p-3">{runResult.text}</pre>
                </div>
              )}
            </div>
          </div>
        )}

        {!editor && !selected && (
          <p className="text-sm text-zinc-500">Select a template to view, run, or duplicate it.</p>
        )}
      </div>
    </div>
  );
}
